import { IItemOption, IItemOptionModifier, IItemOptionModifierWithQuantity } from '@rbi-ctg/menu';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { ModifierTypes } from 'state/product-wizard-v2/types';

import { IModifierSelections } from './types';

export const getSingleSelectedOption = (selections: IModifierSelections) =>
  Object.values(selections)[0];

export const transformSelectedOptionToSelections = (selectedOption: IItemOptionModifier) => ({
  [selectedOption._key]: {
    ...selectedOption,
    quantity: 1,
  },
});

export const transformSelectedValueToSelections = (
  options: IItemOptionModifier[],
  selectedValue: number
) => {
  const selectedOption = options.find(
    option => option.modifierMultiplier?.multiplier === selectedValue
  );

  if (selectedOption) {
    return {
      [selectedOption._key]: {
        ...selectedOption,
        quantity: 1,
      },
    };
  }
  return {};
};

export const getModifierOptionMultipliers = (options: IItemOptionModifier[]) =>
  options.map(option => {
    const multiplierValue = option.modifierMultiplier?.multiplier || 0;
    return {
      label: multiplierValue.toString(),
      value: multiplierValue,
    };
  });

export const getModifierOptionMultiplier = (modifierOption: IItemOptionModifier) =>
  modifierOption.modifierMultiplier?.multiplier || 0;

export const getModifierOptionName = (modifierOption: IItemOptionModifier) =>
  modifierOption.name?.locale || '';

export const getModifierNonNumericOptionLabel = (
  modifierOption: IItemOptionModifier,
  usePrefix: boolean
) =>
  usePrefix
    ? modifierOption.modifierMultiplier?.prefix?.locale
    : getModifierOptionMultiplier(modifierOption).toString();

export const getModifierImage = (modifier: IItemOption): string | undefined => {
  return getModifierOptionImage(modifier.options?.find(Boolean));
};

export const getModifierOptionImage = (modifierOption: IItemOptionModifier | undefined) => {
  const rawImage = modifierOption?.modifierMultiplier?.modifier?.image;
  return rawImage ? JSON.stringify(rawImage) : undefined;
};

const isCheckboxModifier = (modifier: IItemOption): boolean => {
  const hasBothOptions = modifier.options.every(
    opt => getModifierOptionMultiplier(opt) === 0 || getModifierOptionMultiplier(opt) === 1
  );
  return !modifier.singleChoiceOnly && modifier.options.length === 2 && hasBothOptions;
};

const isNonNumericModifier = (modifier: IItemOption): boolean => {
  return !modifier.singleChoiceOnly && !!modifier.displayModifierMultiplierName;
};

const isNumericModifier = (modifier: IItemOption): boolean => {
  return !modifier.singleChoiceOnly && !modifier.displayModifierMultiplierName;
};

const isSingleChoiceModifier = (modifier: IItemOption): boolean => {
  return !!modifier.singleChoiceOnly && modifier.maxAmount === 1;
};

const isMultipleChoiceModifier = (modifier: IItemOption): boolean => {
  return modifier.maxAmount > 1;
};

export const getModifierType = (modifier: IItemOption): ModifierTypes => {
  if (isMultipleChoiceModifier(modifier)) {
    return ModifierTypes.MULTIPLE_CHOICE;
  } else if (isCheckboxModifier(modifier)) {
    return ModifierTypes.CHECKBOX;
  } else if (isNonNumericModifier(modifier)) {
    return ModifierTypes.NON_NUMERIC;
  } else if (isNumericModifier(modifier)) {
    return ModifierTypes.NUMERIC;
  } else if (isSingleChoiceModifier(modifier)) {
    return ModifierTypes.SINGLE_CHOICE;
  }
  return ModifierTypes.NON_NUMERIC;
};

export const computeModifierSelectionLabel = ({
  modifierType,
  modifierName,
  modifierOption,
}: {
  modifierType: ModifierTypes;
  modifierName: string;
  modifierOption: IItemOptionModifierWithQuantity;
}): string => {
  switch (modifierType) {
    case ModifierTypes.CHECKBOX:
      return getModifierOptionMultiplier(modifierOption) === 0
        ? `${t('no')} ${modifierName}`
        : `${modifierOption.modifierMultiplier?.prefix?.locale ?? ''} ${modifierName}`;
    case ModifierTypes.NON_NUMERIC:
      return `${modifierOption.modifierMultiplier?.prefix?.locale ?? ''} ${modifierName}`;
    case ModifierTypes.NUMERIC:
      return `${getModifierOptionMultiplier(modifierOption)} ${modifierName}`;
    case ModifierTypes.SINGLE_CHOICE:
      return getModifierOptionName(modifierOption);
    case ModifierTypes.MULTIPLE_CHOICE:
      return `${modifierOption.quantity > 1 ? modifierOption.quantity : ''} ${getModifierOptionName(
        modifierOption
      )}`;
    default:
      return '';
  }
};

export const logProductModifierCustomization = (
  text: string,
  headerText: string,
  componentKey: string,
  quantity?: number
) => {
  logButtonClick({
    attributes: {
      component: ClickEventComponentNames.PRODUCT_MODIFIER_CUSTOMIZATION,
      componentId: componentKey,
      quantity,
      headerText,
      name: text,
    },
  });
};

export const generateSelections = (
  selections: IModifierSelections,
  option: IItemOptionModifier,
  newValue: number,
  maxGlobal: number
) => {
  // Adds or updates the quantity value
  if (selections.hasOwnProperty(option._key)) {
    selections[option._key].quantity = newValue;
  } else {
    selections[option._key] = { ...option, quantity: newValue };
  }

  // Calculates the total sum for quantity.
  const currentTotal = Object.values(selections).reduce((sum, value) => sum + value.quantity, 0);

  // If the total sum exceeds the maxAmount for the modifier, then update quantity values.
  if (currentTotal > maxGlobal) {
    let excess = currentTotal - maxGlobal;

    // Loops through the selections but skiping the current selection being updated
    // and substracts the necessary quantities from the previous selections.
    for (const key in selections) {
      if (key !== option._key && excess > 0) {
        const reduction = Math.min(selections[key].quantity, excess);
        selections[key].quantity -= reduction;
        excess -= reduction;

        // if the quantity is reduced to 0, then delete the selection from the selections obj.
        if (selections[key].quantity === 0) {
          delete selections[key];
        }
      }
    }
  }

  return selections;
};
